import { useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Checkbox, createTheme, Select, MenuItem, Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { frFR } from '@mui/material/locale';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { customCommonDataGridTheme } from 'assets/styles/themes/DataGrid/common_datagrid';
import { mediaQuery } from './CustomGenericDataGridUtils';
import { useTranslation } from 'react-i18next';

export const RenderEditForecastCell = (props) => {
  const {
    id,
    value,
    field,
    exploitationContext,
    row: { forecast },
  } = props;
  const apiRef = useGridApiContext();
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const handleChange = async (event) => {
    await apiRef.current.setEditCellValue({ id, field, value: event.target.value });
    setOpen(false);
    if (apiRef.current.getCellMode(id, field) === 'edit') {
      apiRef.current.stopCellEditMode({ id, field });
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (apiRef.current.getCellMode(id, field) === 'edit') {
      apiRef.current.stopCellEditMode({ id, field });
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const options = culturesSelectValues(forecast, exploitationContext, t);

  return (
    <Select
      value={value}
      onChange={handleChange}
      size="small"
      autoFocus
      open={open}
      onClose={handleClose}
      onOpen={handleOpen}
      className="forecast_select"
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option.startsWith('message.') ? t(option) : option}
        </MenuItem>
      ))}
    </Select>
  );
};

export const CustomForecastCell = (params, stackedRequestsRef = null, t = null) => {
  const { id, value } = params;
  let hasBeenUpdated = false;

  if (stackedRequestsRef?.current.length) {
    stackedRequestsRef?.current.forEach((request) => {
      if (request?.id === id && 'forecast' in request) {
        hasBeenUpdated = true;
      }
    });
  }

  const displayValue = t && value && value.startsWith('message.') ? t(value) : value;

  return (
    <Box className={`cellBox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}>
      <div>{displayValue}</div>
      {params.isEditable && <ArrowDropDownIcon />}
    </Box>
  );
};

export const CustomForecastHeader = (startYear, type = null) => {
  return (
    <p>
      <span>{`${startYear}-${startYear + 1}`}</span>
    </p>
  );
};

export const customFixedCell = (params, stackedRequestsRef = null) => {
  const { id, value, isEditable, row } = params;
  const disabled = row?.forecast === 'message.noForecast';
  let hasBeenUpdated = false;

  if (stackedRequestsRef?.current.length) {
    stackedRequestsRef?.current.forEach((request) => {
      if (request?.id === id && 'isFixed' in request) {
        hasBeenUpdated = true;
      }
    });
  }
  return (
    <div
      className={`forecast_checkbox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}
      style={disabled ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
    >
      {value ? <CheckIcon /> : <CloseIcon />} {isEditable && !disabled && <ArrowDropDownIcon />}
    </div>
  );
};

export const CustomEditFixedCell = (props) => {
  const { id, value, field, row } = props;
  const disabled = row?.forecast === 'message.noForecast';
  const apiRef = useGridApiContext();
  const [checked, setChecked] = useState(value);

  const handleChange = async (event) => {
    setChecked(event.target.checked);
    await apiRef.current.setEditCellValue({ id, field, value: event.target.checked });
    apiRef.current.stopCellEditMode({ id, field });
  };

  const label = { inputProps: { 'aria-label': 'Fixed Culture Checkbox' } };

  return (
    <Box sx={{ display: 'inline-flex', cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <Checkbox
        {...label}
        checked={checked}
        onClick={handleChange}
        size="small"
        disabled={disabled}
        sx={{ '&.Mui-disabled': { display: 'inline-flex' } }}
      />
    </Box>
  );
};

export const culturesSelectValues = (forecast, exploitationContext) => {
  const noForecastKey = 'message.noForecast';
  const culturesMain = exploitationContext?.cultures
    ?.filter((culture) => culture.cultureType === 'main')
    .map((culture) => culture.name);

  if (exploitationContext.cultures) {
    if (!forecast || forecast === noForecastKey) {
      return [noForecastKey, ...culturesMain];
    }
    return [...culturesMain];
  }
};

export const flattenParcelsWithChildren = (data) => {
  return data
    .map((parcel) => {
      if (parcel.children) {
        const clone = structuredClone(parcel);
        delete parcel.children;
        return [...clone.children, parcel];
      }
      return parcel;
    })
    .flat();
};

export const updateExploitationContext = (setExploitationContext, data) => {
  const flattenedData = flattenParcelsWithChildren(data);
  setExploitationContext((prevExploitationContext) => {
    const filterDeletedForecast = prevExploitationContext?.parcels?.filter(
      (prevParcel) => !flattenedData.some((datum) => prevParcel?.id === datum.id),
    );
    return {
      ...prevExploitationContext,
      parcels: [...filterDeletedForecast, ...flattenedData],
    };
  });
};

export const themeFilters = createTheme({
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          m: 1,
          width: 300,
          border: '1px solid #e69636 !important',
          borderRadius: '5px',
          maxHeight: '50px !important',
          margin: '5px',
          '.MuiOutlinedInput-root.Mui-focused fieldset': {
            borderColor: '#e69636 !important',
            top: '-2px !important',
          },
          '.MuiOutlinedInput-root.Mui-focused legend': {
            display: 'none !important',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#e69636 !important',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          maxHeight: '50px !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 0, 0, 0.87) !important',
          maxHeight: '50px !important',
          '.MuiSelect-select': {
            padding: '15px 25px 10px 13px !important',
          },
          '.MuiSelect-select.Mui-disabled': {
            WebkitTextFillColor: 'rgba(0, 0, 0, 0.65) !important',
          },
          '.MuiSvgIcon-root.MuiSelect-icon': {
            color: '#277134 !important',
          },
          'legend span': {
            display: 'none !important',
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '.MuiSvgIcon-root.MuiSelect-icon': {
            color: '#FFF !important',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#e69636 !important',
          '&.Mui-checked': {
            color: '#277134 !important',
          },
        },
      },
    },
  },
});

export const customDataGridResultTheme = createTheme(customCommonDataGridTheme, {
  components: {
    MuiDataGrid: {
      styleOverrides: {
        toolbarContainer: {
          '& .contain_switch_effect': {
            marginLeft: 'auto',
          },
        },
        root: {
          border: '1px solid #277134',
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
          borderBottomLeftRadius: '0px !important',
          borderBottomRightRadius: '0px !important',
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
            border: 'none',
            outline: 'none',
          },
          '& .MuiDataGrid-scrollbar': {
            zIndex: 1,
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
            border: 'none',
            outline: 'none',
            '& :focus-visible': {
              border: 'none',
              outline: 'none',
            },
          },
          '& .MuiDataGrid-virtualScroller': {
            maxHeight: '100vh',
            overflowY: 'auto !important',
            overflowX: 'hidden !important',
            [`@media (max-width: ${mediaQuery.breakpoints.values.lg}px)`]: {
              maxHeight: '51vh',
            },
          },
          '& .MuiDataGrid-columnHeadersInner': { width: '100%' },
          '& .MuiDataGrid-columnHeadersInner .css-yrdy0g-MuiDataGrid-columnHeaderRow': {
            width: '100%',
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
            boxShadow: '0 1px 5px 0 rgba(0, 0, 0, 0.2)',
            backgroundColor: 'transparent ',
          },
        },
        main: {
          fontFamily: 'Rubik-Medium',
          fontSize: '16px',
          color: '#707070',
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
          borderBottomLeftRadius: '0px !important',
          borderBottomRightRadius: '0px !important',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#e69636',
            color: 'white',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#e69636 !important',
            color: 'white',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e69636',
          borderLeft: '1px solid #e69636',
          borderRight: '1px solid #e69636',
          fontFamily: 'Rubik',
          fontSize: '14px',
          color: '#707070',
          ':hover': {
            color: '#e69636',
            fontFamily: 'Rubik-Medium',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingBottom: '1px',
          paddingTop: '1px',
          '& li:nth-of-type(1)': {
            borderTop: '1px solid #e69636',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          color: '#e69636 !important',
          '&.Mui-checked': {
            color: '#277134 !important',
            '&.MuiDataGrid-columnHeaderTitleContainer': {
              color: '#277134 !important',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: 'unset !important',
          borderWidth: '0px !important',
        },
      },
    },
  },
});

export const parcelSynthesisTheme = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: '1px solid #277134',
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
          borderBottomLeftRadius: '0px !important',
          borderBottomRightRadius: '0px !important',
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
            border: 'none',
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeadersInner': { width: '100%' },
          '& .MuiDataGrid-columnHeadersInner .css-yrdy0g-MuiDataGrid-columnHeaderRow': {
            width: '100%',
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
            boxShadow: '0 1px 5px 0 rgba(0, 0, 0, 0.2)',
            backgroundColor: 'transparent',
            overflow: 'visible',
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
            border: 'none',
            outline: 'none',
            '& :focus-visible': {
              border: 'none',
              outline: 'none',
            },
          },
          '& .MuiDataGrid-virtualScroller': {
            maxHeight: '100vh',
            overflowY: 'auto !important',
            overflowX: 'hidden !important',
            [`@media (max-width: ${mediaQuery.breakpoints.values.lg}px)`]: {
              maxHeight: '51vh',
            },
          },
          fontFamily: 'Rubik-Medium',
          fontSize: '16px',
          color: '#707070',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#277134',
            color: 'white',
          },
          '& .MuiDataGrid-columnHeader ': {
            backgroundColor: '#277134 !important',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingBottom: '1px',
          paddingTop: '1px',
          '& li:nth-of-type(1)': {
            borderTop: '1px solid #e69636',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e69636',
          borderLeft: '1px solid #e69636',
          borderRight: '1px solid #e69636',
          fontFamily: 'Rubik',
          fontSize: '14px',
          color: '#707070',
          ':hover': {
            color: '#e69636',
            fontFamily: 'Rubik-Medium',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: 'unset !important',
          borderWidth: '0px !important',
        },
      },
    },
  },
});

export const customDataGridResultSimulation = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: '1px solid #277134',
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
          borderBottomLeftRadius: '0px !important',
          borderBottomRightRadius: '0px !important',
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
            border: 'none',
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader ': {
            backgroundColor: '#277134 !important',
          },
          '& .MuiDataGrid-row:not([aria-level="1"]) .MuiDataGrid-treeDataGroupingCell': {
            marginLeft: 'calc(var(--DataGrid-t-spacing-unit) * 1.5) !important',
          },
        },
        main: {
          fontFamily: 'Rubik-Medium',
          fontSize: '16px',
          color: '#707070',
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
          borderBottomLeftRadius: '0px !important',
          borderBottomRightRadius: '0px !important',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#277134',
            color: 'white',
          },
        },
      },
    },
  },
});

export const customForecastDataGridTheme = createTheme(
  customCommonDataGridTheme,
  {
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: '1px solid #277134 !important',
            '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
              border: 'none',
              outline: 'none',
            },
            '& .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
              border: 'none',
              outline: 'none',
              '& :focus-visible': {
                border: 'none',
                outline: 'none',
              },
            },
            '& .MuiDataGrid-virtualScroller': {
              maxHeight: '100vh',
              overflowY: 'auto !important',
              overflowX: 'hidden !important',
              [`@media (max-width: ${mediaQuery.breakpoints.values.lg}px)`]: {
                maxHeight: '43vh',
              },
            },
            '& .MuiDataGrid-columnHeaderTitleContainer .MuiCheckbox-root': {
              color: '#277134 !important',
            },
            '& .MuiDataGrid-cell': {
              whiteSpace: 'pre-wrap !important',
              textAlign: 'center',
            },
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { padding: '5px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { padding: '9px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { padding: '14px' },
            '& .MuiDataGrid-ParcelChild--row': {
              backgroundColor: '#d6d6d6 !important',
            },
          },
          main: {
            fontFamily: 'Rubik-Medium',
            fontSize: '16px',
            color: '#707070',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#e69636',
              color: 'white',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            border: '1px solid #e69636',
            fontFamily: 'Rubik',
            fontSize: '14px',
            color: '#707070',
            ':hover': {
              color: '#e69636',
              fontFamily: 'Rubik-Medium',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          colorPrimary: {
            color: '#e69636 !important',
            '&.Mui-checked': {
              color: '#277134 !important',
            },
            '&.Mui-disabled': {
              display: 'none',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: 'unset !important',
            borderWidth: '0px !important',
          },
        },
      },
    },
  },
  frFR,
);

export const rotateEffectTheme = createTheme(customDataGridResultTheme, {
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#277134 !important',
            color: 'white',
          },
          '& .MuiDataGrid-columnHeader.cropEffect-header--intermediate': {
            backgroundColor: '#e69636 !important',
          },
        },
      },
    },
  },
});

export const isSameAsOriginal = (field, parcels, updatedRow) =>
  parcels.some((parcel) => {
    if (field === 'surface') {
      return parcel.id === updatedRow.id && Number(parcel?.[field]) === Number(updatedRow[field]);
    }
    if (
      field === 'isIrrigable' ||
      field === 'name' ||
      field === 'organicState' ||
      field === 'conversionDate'
    ) {
      return parcel.id === updatedRow.id && parcel?.[field] === updatedRow[field];
    }

    return parcel.id === updatedRow.id && parcel?.[field]?.name === updatedRow[field];
  });
